import { useRouter } from 'next/router';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';
import { Images404WhiskyMissingWebp } from '@/assets/images_404.assets';
import { GetStaticPropsContext } from 'next';
import useHasMounted from '@/hooks/useHasMounted';
import getServerSideTranslations from '@/utilites/translations/get-server-side-translations.translations.utils';
import ConditionalComponent from '@/components/common/ConditionalComponent';
import { PrimaryButton, PrimaryButtonLink } from '@/components/common/PrimaryButton';
import { Col, Container, Row } from 'react-bootstrap';
import Layout404 from '@/components/layouts/layout404';

const Custom404 = () => {
  const { back } = useRouter();
  const hasMounted = useHasMounted();
  const { t } = useTranslation('common');

  return (
    <Container className="tw-py-20">
      <Row className="tw-items-center tw-gap-y-10">
        <Col md={6}>
          <Image
            src={Images404WhiskyMissingWebp}
            alt="404"
            width={1536}
            height={768}
            className="tw-h-full tw-w-full tw-rounded-[20px] tw-object-cover tw-object-center max-nine:tw-max-h-72"
          />
        </Col>
        <Col md={6}>
          <Row className="tw-gap-8">
            <Col xs={12} className="tw-text-center">
              <Row>
                <Col xs={12}>
                  <h1 className="tw-m-0 tw-text-8xl tw-font-bold max-nine:tw-text-xl">404</h1>
                </Col>
                <Col xs={12} className="tw-text-center">
                  <h3 className="tw-m-0 tw-text-4xl tw-font-bold max-nine:tw-text-xl">
                    {translationFallback('page_not_found', t('page_not_found'), 'Page not found')}
                  </h3>
                </Col>
                <Col xs={12} className="tw-text-center">
                  <p className="text-center tw-m-0 tw-text-2xl tw-font-medium max-nine:!tw-text-base">
                    {translationFallback(
                      'this_page_doesnt_exist_or_has_been_removed',
                      t('this_page_doesnt_exist_or_has_been_removed'),
                      "This Page doesn't exist or has been removed"
                    )}
                  </p>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="tw-text-center">
              <ConditionalComponent
                condition={hasMounted && window.history.length > 1}
                trueComponent={
                  <PrimaryButton onClick={back} className="tw-mx-auto tw-w-full tw-max-w-60">
                    {translationFallback('go_back', t('go_back'), 'Go Back')}
                  </PrimaryButton>
                }
                falseComponent={
                  <PrimaryButtonLink href="/" className="tw-mx-auto tw-w-full tw-max-w-60">
                    {translationFallback('go_back', t('go_back'), 'Go Back')}
                  </PrimaryButtonLink>
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
  const serverSideTranslations = await getServerSideTranslations({ locale: ctx.locale }, [
    'common',
  ]);
  return {
    props: {
      ...serverSideTranslations,
      is404: true,
      isStaticPage: true,
    },
  };
};

Custom404.getLayout = (page: any, pageProps: any) => <Layout404 {...pageProps}>{page}</Layout404>;

export default Custom404;
